import React, { useState, useEffect } from 'react';
import { jugendaemterAPI, assignmentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, Landmark, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fehlertext } from '../lib/fehlertext';

const PAUSCHALE_DEFAULT = 'Pauschale für Abstimmungsgespräche';
const SQ = ['sq1', 'sq2', 'sq3', 'sq4'];
const emptyForm = { name: '', lexware_kundennummer: '', satz_q1: '', satz_q2: '', pauschale_aktiv: false, pauschale_bezeichnung: PAUSCHALE_DEFAULT, pauschale_stunden: '1', vier_stufen: false, satz_sq1: '', satz_sq2: '', satz_sq3: '', satz_sq4: '' };
const euro = (v) => Number(v || 0).toFixed(2).replace('.', ',');

function JugendamtRow({ item, onEdit, onDelete }) {
  return (
    <tr className="border-t border-slate-100" data-testid={'jugendamt-row-' + item.id}>
      <td className="py-2.5 px-3 text-sm font-medium text-slate-900">{item.name}</td>
      <td className="py-2.5 px-3 text-sm text-slate-600">{item.lexware_kundennummer}</td>
      {item.vier_stufen ? (
        <td colSpan={2} className="py-2.5 px-3 text-xs text-slate-600" data-testid={'jugendamt-sq-' + item.id}>
          <span className="font-semibold text-slate-700">SQ1–SQ4:</span> {SQ.map(k => euro(item['satz_' + k])).join(' / ')} €
        </td>
      ) : (
        <>
          <td className="py-2.5 px-3 text-sm text-slate-600">{Number(item.satz_q1).toFixed(2)} €</td>
          <td className="py-2.5 px-3 text-sm text-slate-600">{Number(item.satz_q2).toFixed(2)} €</td>
        </>
      )}
      <td className="py-2.5 px-3 text-sm text-slate-600" data-testid={'jugendamt-pauschale-' + item.id}>
        {item.pauschale_aktiv
          ? `${Number(item.pauschale_stunden || 0).toFixed(2).replace('.', ',')} h · ${item.pauschale_bezeichnung || ''}`
          : '—'}
      </td>
      <td className="py-2.5 px-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(item)} data-testid={'edit-jugendamt-' + item.id}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-800" onClick={() => onDelete(item)} data-testid={'delete-jugendamt-' + item.id}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

const AdminJugendaemter = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [warVierStufen, setWarVierStufen] = useState(false);
  const [zuordnungen, setZuordnungen] = useState([]);

  const load = () => {
    jugendaemterAPI.getAll().then(res => setItems(res.data)).catch(() => toast.error('Fehler beim Laden')).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => { setEditId(null); setWarVierStufen(false); setForm(emptyForm); setDialogOpen(true); };

  // Auftrag SQ1: betroffene aktive Zuordnungen, die beim Einschalten noch Q1/Q2 haben
  const betroffen = zuordnungen.filter(a => editId && a.jugendamt_id === editId && a.is_active !== false
    && !['SQ1', 'SQ2', 'SQ3', 'SQ4'].includes(String(a.qualifikation || '').toUpperCase())).length;
  const vierStufenUmschalten = (v) => {
    setForm(f => ({ ...f, vier_stufen: v }));
    if (v && editId) assignmentsAPI.getAll().then(res => setZuordnungen(res.data || [])).catch(() => setZuordnungen([]));
  };
  const openEdit = (item) => {
    setEditId(item.id);
    setWarVierStufen(!!item.vier_stufen);
    setForm({
      name: item.name ?? '', lexware_kundennummer: item.lexware_kundennummer ?? '',
      satz_q1: String(item.satz_q1 ?? ''), satz_q2: String(item.satz_q2 ?? ''),
      pauschale_aktiv: !!item.pauschale_aktiv,
      pauschale_bezeichnung: item.pauschale_bezeichnung ?? PAUSCHALE_DEFAULT,
      pauschale_stunden: String(item.pauschale_stunden ?? '1'),
      vier_stufen: !!item.vier_stufen,
      ...Object.fromEntries(SQ.map(k => ['satz_' + k, String(item['satz_' + k] ?? '')])),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    const stunden = parseFloat(String(form.pauschale_stunden).replace(',', '.'));
    if (form.pauschale_aktiv && (!Number.isFinite(stunden) || stunden <= 0)) {
      toast.error('Bei aktiver Pauschale müssen die Pauschal-Stunden größer als 0 sein.');
      return;
    }
    const sq = Object.fromEntries(SQ.map(k => {
      const n = parseFloat(String(form['satz_' + k]).replace(',', '.'));
      return ['satz_' + k, Number.isFinite(n) ? n : null];
    }));
    if (form.vier_stufen && SQ.some(k => !(sq['satz_' + k] > 0))) {
      toast.error('Bitte alle vier Sätze SQ1 bis SQ4 eintragen.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      lexware_kundennummer: form.lexware_kundennummer.trim(),
      satz_q1: parseFloat(form.satz_q1) || 0,
      satz_q2: parseFloat(form.satz_q2) || 0,
      pauschale_aktiv: !!form.pauschale_aktiv,
      pauschale_bezeichnung: (form.pauschale_bezeichnung || '').trim() || PAUSCHALE_DEFAULT,
      pauschale_stunden: Number.isFinite(stunden) ? stunden : 0,
      vier_stufen: !!form.vier_stufen,
      ...sq,
    };
    try {
      setSaving(true);
      if (editId) {
        await jugendaemterAPI.update(editId, payload);
        toast.success('Jugendamt aktualisiert');
      } else {
        await jugendaemterAPI.create(payload);
        toast.success('Jugendamt erstellt');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(fehlertext(e, 'Fehler beim Speichern')); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await jugendaemterAPI.delete(deleteDialog.id);
      toast.success('Jugendamt gelöscht');
      setDeleteDialog({ open: false, id: null, name: '' });
      load();
    } catch (e) { toast.error(fehlertext(e, 'Fehler beim Löschen')); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;

  const rows = items.map(item => (
    <JugendamtRow key={item.id} item={item} onEdit={openEdit} onDelete={(it) => setDeleteDialog({ open: true, id: it.id, name: it.name })} />
  ));

  return (
    <div className="space-y-6" data-testid="admin-jugendaemter-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">Jugendämter & Sätze</h1>
          <p className="text-slate-500 mt-1">Stundensätze je Jugendamt für die Rechnungserstellung verwalten</p>
        </div>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={openCreate} data-testid="create-jugendamt-button">
          <Plus className="h-4 w-4 mr-2" />Neues Jugendamt
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="jugendaemter-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="py-3 px-3 text-xs font-semibold text-slate-600 uppercase">Jugendamt</th>
                  <th className="py-3 px-3 text-xs font-semibold text-slate-600 uppercase">Lexware-Kundennr.</th>
                  <th className="py-3 px-3 text-xs font-semibold text-slate-600 uppercase">Satz Q1 (€)</th>
                  <th className="py-3 px-3 text-xs font-semibold text-slate-600 uppercase">Satz Q2 (€)</th>
                  <th className="py-3 px-3 text-xs font-semibold text-slate-600 uppercase">Pauschale</th>
                  <th className="py-3 px-3 text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
          {items.length === 0 && <p className="text-center text-slate-400 py-8">Noch keine Jugendämter angelegt.</p>}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? 'Jugendamt bearbeiten' : 'Neues Jugendamt'}</DialogTitle>
            <DialogDescription>Stammdaten und Stundensätze für die Rechnungserstellung</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z.B. Wetteraukreis" data-testid="jugendamt-name-input" />
            </div>
            <div>
              <Label>Lexware-Kundennummer</Label>
              <Input value={form.lexware_kundennummer} onChange={e => setForm(f => ({ ...f, lexware_kundennummer: e.target.value }))} placeholder="z.B. 10008" data-testid="jugendamt-kundennr-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="jugendamt-vier-stufen">Vier Stufen (SQ1–SQ4) statt Q1/Q2</Label>
              <Switch id="jugendamt-vier-stufen" checked={!!form.vier_stufen} onCheckedChange={vierStufenUmschalten} data-testid="jugendamt-vier-stufen-switch" />
            </div>
            {form.vier_stufen && editId && !warVierStufen && (
              <div className="flex gap-2 text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-1.5" data-testid="jugendamt-vier-stufen-hinweis">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Bestehende Zuordnungen zu diesem Amt haben noch Q1/Q2 und brauchen danach SQ1–SQ4, sonst fehlt der Satz für die Rechnung. Betroffene aktive Zuordnungen: <strong data-testid="jugendamt-vier-stufen-anzahl">{betroffen}</strong></span>
              </div>
            )}
            {form.vier_stufen ? (
              <div className="grid grid-cols-2 gap-3">
                {SQ.map(k => (
                  <div key={k}>
                    <Label>Satz {k.toUpperCase()} (€)</Label>
                    <Input type="number" step="0.01" value={form['satz_' + k]} onChange={e => setForm(f => ({ ...f, ['satz_' + k]: e.target.value }))} data-testid={'jugendamt-' + k + '-input'} />
                  </div>
                ))}
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Satz Q1 (€)</Label>
                <Input type="number" step="0.01" value={form.satz_q1} onChange={e => setForm(f => ({ ...f, satz_q1: e.target.value }))} placeholder="40.15" data-testid="jugendamt-q1-input" />
              </div>
              <div>
                <Label>Satz Q2 (€)</Label>
                <Input type="number" step="0.01" value={form.satz_q2} onChange={e => setForm(f => ({ ...f, satz_q2: e.target.value }))} placeholder="47.26" data-testid="jugendamt-q2-input" />
              </div>
            </div>
            )}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Pauschale aktiv</Label>
                  <p className="text-xs text-slate-500">Zusatzposition je Stundenzettel, eigene Rechnungszeile.</p>
                </div>
                <Switch checked={!!form.pauschale_aktiv} onCheckedChange={v => setForm(f => ({ ...f, pauschale_aktiv: v }))} data-testid="jugendamt-pauschale-aktiv-input" />
              </div>
              {form.pauschale_aktiv && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Bezeichnung</Label>
                    <Input value={form.pauschale_bezeichnung} onChange={e => setForm(f => ({ ...f, pauschale_bezeichnung: e.target.value }))} placeholder={PAUSCHALE_DEFAULT} data-testid="jugendamt-pauschale-bezeichnung-input" />
                    {!(form.pauschale_bezeichnung || '').trim() && (
                      <p className="text-xs text-amber-600 mt-1" data-testid="jugendamt-pauschale-hinweis">
                        Leer: es wird „{PAUSCHALE_DEFAULT}" verwendet.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Stunden je Monat</Label>
                    <Input type="number" step="0.25" min="0" value={form.pauschale_stunden} onChange={e => setForm(f => ({ ...f, pauschale_stunden: e.target.value }))} placeholder="1" data-testid="jugendamt-pauschale-stunden-input" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={handleSave} disabled={saving} data-testid="save-jugendamt-button">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editId ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={open => { if (!open) setDeleteDialog({ open: false, id: null, name: '' }); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Jugendamt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie „{deleteDialog.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-jugendamt">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminJugendaemter;
